/**
 *  App level synchronization to our swift plugin that handles native audio
 *  playback on iOS.
 *
 *  This model is responsible for saving progress updates, position updates,
 *  updating listening analytics, etc.
 */

import { auth } from '@/services/auth'
import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'
import { Mixpanel } from '@/analytics/mixpanel'
import type {
  Doc,
  DocContent,
  DocListItem,
  DocWithContent,
} from '@/models/interfaces'
import { isNativeAudioAvailable, nativeAudioBridge } from './native-audio'
import { playerSession } from './session'
import { saveAudioChapterProgress } from './control.chapter.progress'
import type { NativePlaybackProgress } from './native-audio.types'
import {
  cacheNativeProgressLocally,
  canApplyNativeReadingPosition,
  forgetAppliedNativeReading,
  sendNativeReadingPosition,
} from './native-reading-position'

// Send current progress update information to our backend and also to mixpanel
export async function deliverNativeProgress(
  progress: NativePlaybackProgress,
  expectedUserId: string,
  currentUserId: () => string | null,
  sendToBackend: (progress: NativePlaybackProgress) => Promise<void>,
  sendToMixpanel: (progress: NativePlaybackProgress) => void,
  clear: (id: string) => Promise<void>,
): Promise<void> {
  if (
    progress.document.userId !== expectedUserId ||
    currentUserId() !== expectedUserId
  ) {
    return
  }
  await sendToBackend(progress)
  if (currentUserId() !== expectedUserId) {
    return
  }
  await clear(progress.id)
  if (currentUserId() !== expectedUserId) {
    return
  }
  sendToMixpanel(progress)
}

function userId(): string | null {
  return auth.getUser()?.id ?? null
}

function assertUser(expected: string): void {
  if (userId() !== expected) {
    throw new Error('User changed during audio sync')
  }
}

async function sendProgressToBackend(
  progress: NativePlaybackProgress,
): Promise<void> {
  const metadata = progress.document
  assertUser(metadata.userId)
  const activeDoc = playerSession.value.doc
  const doc = activeDoc?.id === metadata.docId ? activeDoc : undefined
  const position = Math.floor(progress.position)
  await backend.saveAudioPosition(
    (doc ?? { id: metadata.docId }) as Doc,
    position,
  )
  assertUser(metadata.userId)
  if (doc && playerSession.value.doc === doc) {
    doc.user.audio_position = position
  }
  const deliveredChapterIds: string[] = []
  try {
    for (const id of progress.completedChapterIds) {
      const idx = doc?.content.findIndex((page) => page.id === id) ?? -1
      if (idx >= 0 && playerSession.value.doc === doc && doc) {
        await saveAudioChapterProgress(doc, idx)
      } else if (!auth.isFreePlan() || metadata.isFree) {
        await backend.saveProgress({ id } as DocContent)
      }
      assertUser(metadata.userId)
      deliveredChapterIds.push(id)
    }
  } finally {
    if (deliveredChapterIds.length) {
      await nativeAudioBridge.clearProgress({
        userId: metadata.userId,
        id: progress.id,
        chapterIds: deliveredChapterIds,
      })
    }
  }
  assertUser(metadata.userId)
  const currentDoc = playerSession.value.doc === doc ? doc : undefined
  await sendNativeReadingPosition(progress, currentDoc, () =>
    assertUser(metadata.userId),
  )
}

function sendProgressToMixpanel(progress: NativePlaybackProgress): void {
  const metadata = progress.document
  const activeDoc = playerSession.value.doc
  const doc =
    activeDoc?.id === metadata.docId
      ? activeDoc
      : ({
          id: metadata.docId,
          title: metadata.title,
          is_free: metadata.isFree,
        } as DocListItem)
  const totalSeconds = Math.round(progress.listenedSeconds)
  const offlineSeconds = Math.min(
    totalSeconds,
    Math.round(Math.max(0, progress.offlineListenedSeconds)),
  )
  const onlineSeconds = totalSeconds - offlineSeconds
  if (onlineSeconds > 0) {
    Mixpanel.trackAudioListen(doc, onlineSeconds, metadata.isDownload, false)
  }
  if (offlineSeconds > 0) {
    Mixpanel.trackAudioListen(doc, offlineSeconds, metadata.isDownload, true)
  }
}

// Data syncronisation promise is used to allow only one update at a time, in
// order
let running: Promise<void> | undefined
let initialized = false

// Start a syncronisation run, if one isn't already running
export function requestNativeProgressSync(
  closingDoc?: DocWithContent,
): Promise<void> {
  if (running) {
    return running
  }
  const currentUserId = userId()
  if (!currentUserId || document.hidden) {
    return Promise.resolve()
  }
  running = runNativeProgressSync(currentUserId, closingDoc)
  return running
}

async function runNativeProgressSync(
  expectedUserId: string,
  closingDoc?: DocWithContent,
): Promise<void> {
  try {
    await performNativeProgressSync(expectedUserId, closingDoc)
  } finally {
    running = undefined
  }
}

export async function requestFinalNativeProgressSync(
  closingDoc: DocWithContent,
): Promise<void> {
  const joinedEarlierRun = running !== undefined
  await requestNativeProgressSync(closingDoc)
  if (joinedEarlierRun) {
    await requestNativeProgressSync(closingDoc)
  }
}

// Ask our swift plugin for an audio progress update, and then sends the
// updated data to mixpanel and the backend if we get one
async function performNativeProgressSync(
  expectedUserId: string,
  closingDoc?: DocWithContent,
): Promise<void> {
  try {
    const { progress } = await nativeAudioBridge.pendingProgress({
      userId: expectedUserId,
    })
    const blockedDocumentIds = new Set<string>()
    for (const item of progress) {
      assertUser(expectedUserId)
      if (item.document.userId !== expectedUserId) {
        continue
      }
      const activeDoc = playerSession.value.doc
      const doc =
        activeDoc?.id === item.document.docId
          ? activeDoc
          : closingDoc?.id === item.document.docId
            ? closingDoc
            : undefined
      if (doc?.id === item.document.docId) {
        try {
          await cacheNativeProgressLocally(
            item,
            doc,
            canApplyNativeReadingPosition(item, doc.user),
            () => userId() === expectedUserId,
          )
        } catch (error) {
          Sentry.captureException(error)
        }
      }
      if (blockedDocumentIds.has(item.document.docId)) {
        continue
      }
      if (!navigator.onLine) {
        continue
      }
      try {
        await deliverNativeProgress(
          item,
          expectedUserId,
          userId,
          sendProgressToBackend,
          sendProgressToMixpanel,
          async (id) => {
            await nativeAudioBridge.clearProgress({
              userId: expectedUserId,
              id,
            })
            forgetAppliedNativeReading(id)
          },
        )
      } catch (error) {
        blockedDocumentIds.add(item.document.docId)
        Sentry.captureException(error)
      }
    }
  } catch (error) {
    Sentry.captureException(error)
  }
}

// Update mixpanel and the backend with audio progress every 10 seconds
export function startNativeProgressSync(): void {
  if (!userId() || !isNativeAudioAvailable()) {
    return
  }
  if (!initialized) {
    initialized = true
    window.addEventListener('online', () => void requestNativeProgressSync())
    document.addEventListener(
      'visibilitychange',
      () => void requestNativeProgressSync(),
    )
    window.setInterval(() => void requestNativeProgressSync(), 10000)
  }
  void requestNativeProgressSync()
}
